import React, { useState, useEffect, memo } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import config from '../config/config';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import {
  SlidersHorizontal, Grid3x3, List, ChevronDown, X, Star, Minus, Plus, Heart,
  ShoppingBag, ChevronRight, ChevronLeft, Package, Truck, CheckCircle, XCircle,
  Clock, Search, Filter, Download, Eye, RotateCcw, Mail, Lock, User, Phone, EyeOff
} from 'lucide-react';
import ShareableNav from "../components/ShareableNav";
import FlashBar from "../components/FlashBar";
import NavigationBar from "../components/NavigationBar";
import FooterSection from "../components/FooterSection";
import CartSection from "../components/CartSection";
import SearchWidget from "../components/SearchWidget";

const AuthForm = memo(({
  isLoginMode,
  formData,
  setFormData,
  formErrors,
  setFormErrors,
  authLoading,
  authMessage,
  showPassword,
  setShowPassword,
  showConfirmPassword,
  setShowConfirmPassword,
  handleAuthSubmit,
  handleOtpSubmit,
  toggleMode,
  handleAuthChange,
  isOtp,
  otp,
  setOtp,
  setIsOtp,
  send_otp,
  featuredBg,
  userInfo,
  isForgot,
  setIsForgot,
  forgotEmail,
  setForgotEmail,
  handleForgotSubmit,
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 sm:px-6">
      <div className="max-w-lg mx-auto">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&family=Quicksand:wght@400;500;600;700&display=swap');
          html,body{
            font-family: ${config.FONT_FAMILY}, sans-serif !important;
          }
        `}</style>
        <br/><br/><br/><br/><br/>

        <div className="bg-white mt-[-10%] md:mt-[0px] rounded-2xl shadow-xl p-6 sm:p-10">
          {!userInfo && (
            <div className="bg-blue-300 px-3 py-3 rounded mb-4">
              <strong className="text-gray-900">Sign in to complete checkout</strong>
            </div>
          )}

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {isForgot
                ? 'Reset Password'
                : isLoginMode
                  ? 'Welcome Back'
                  : 'Create Your Account'}
            </h1>
            <p className="text-gray-600">
              {isForgot
                ? 'Enter your email for verification. An OTP will be sent to your phone'
                : isLoginMode
                  ? 'Sign in to continue shopping'
                  : 'Join us and start shopping today'}
            </p>
          </div>

          {authMessage && (
            <div className={`mb-6 p-4 rounded-xl text-center font-medium border ${
              authMessage.toLowerCase().includes('success')
                ? 'bg-green-50 text-green-700 border-green-200'
                : 'bg-red-50 text-red-700 border-red-200'
            }`}>
              {authMessage}
            </div>
          )}

          {isForgot && !isOtp && (
            <form onSubmit={handleForgotSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                    placeholder="name@example.com"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={authLoading}
                className="w-full py-4 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2"
                style={{ backgroundColor: featuredBg }}
              >
                {authLoading ? 'Sending...' : 'Send Reset Code'}
              </button>

              <div className="text-center text-sm text-gray-600 mt-4">
                <button
                  type="button"
                  onClick={() => { setIsForgot(false); }}
                  className="font-semibold bg-gray-900 text-white rounded px-2 py-1 hover:underline"
                >
                  Back to Sign In
                </button>
              </div>
            </form>
          )}

          {isOtp && (
            <form onSubmit={handleOtpSubmit} className="space-y-5">
              <div>
                <label>OTP</label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="Enter 4-digit PIN"
                  className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-2xl text-2xl font-bold text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-amber-400"
                  maxLength="4"
                  minLength="4"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full py-4 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2"
                style={{ backgroundColor: featuredBg }}
              >
                {authLoading ? 'Processing...' : isLoginMode ? 'Verify' : 'Verify Otp'}
              </button>
            </form>
          )}

          {!isOtp && !isForgot && (
            <form onSubmit={handleAuthSubmit} className="space-y-5">
              {!isLoginMode && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleAuthChange}
                        className={`w-full pl-11 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all ${
                          formErrors.fullName ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="John Doe"
                        autoComplete="name"
                      />
                    </div>
                    {formErrors.fullName && <p className="mt-1 text-sm text-red-600">{formErrors.fullName}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleAuthChange}
                        className={`w-full pl-11 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all ${
                          formErrors.phone ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="+254 712 345 678"
                        autoComplete="tel"
                      />
                    </div>
                    {formErrors.phone && <p className="mt-1 text-sm text-red-600">{formErrors.phone}</p>}
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleAuthChange}
                    className={`w-full pl-11 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all ${
                      formErrors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="name@example.com"
                    autoComplete="email"
                  />
                </div>
                {formErrors.email && <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleAuthChange}
                    className={`w-full pl-11 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all ${
                      formErrors.password ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="••••••••"
                    autoComplete={isLoginMode ? 'current-password' : 'new-password'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(prev => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {formErrors.password && <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>}
              </div>

              {!isLoginMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleAuthChange}
                      className={`w-full pl-11 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all ${
                        formErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="••••••••"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(prev => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {formErrors.confirmPassword && <p className="mt-1 text-sm text-red-600">{formErrors.confirmPassword}</p>}
                </div>
              )}

              {formErrors.general && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm text-center">
                  {formErrors.general}
                </div>
              )}

              <button
                type="submit"
                disabled={authLoading}
                className="w-full py-4 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2"
                style={{ backgroundColor: featuredBg }}
              >
                {authLoading ? 'Processing...' : isLoginMode ? 'Sign In' : 'Create Account'}
              </button>

              <div className="text-center text-sm text-gray-600 mt-6">
                {isLoginMode ? "Don't have an account?" : 'Already have an account?'}{' '}
                <button
                  type="button"
                  onClick={toggleMode}
                  className="font-semibold bg-gray-900 text-white rounded px-2 py-1 hover:underline"
                >
                  {isLoginMode ? 'Sign up' : 'Sign in'}
                </button>
              </div>

              <div
                className="text-center text-sm text-blue-600 mt-6 cursor-pointer"
                onClick={() => { setIsForgot(true); }}
              >
                Forgot Password? Reset
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
});

AuthForm.displayName = 'AuthForm';

const CACHE_KEY_LAYOUT = 'layout_settings_cache_authpaged';
const CACHE_TTL = 24 * 60 * 90 * 1000;

const ShopPage = () => {
  const { cartItems } = useCart();
  const navigate = useNavigate();
  const userInfo = localStorage.getItem('userInfor');
  const token = localStorage.getItem('token');
  let featuredBg = localStorage.getItem('featuredBg') || config.BASE_COLOR;
  const activeBg = localStorage.getItem('activeBg') || config.BASE_COLOR;
  if (featuredBg == 'bg-gray-400') {
    featuredBg = config.BASE_COLOR;
  }

  const [otp, setOtp] = useState('');
  const [isOtp, setIsOtp] = useState(false);
  const [isForgot, setIsForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const [isLoginMode, setIsLoginMode] = useState(true);
  const [formData, setFormData] = useState({
    fullName: '', email: '', phone: '', password: '', confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [authLoading, setAuthLoading] = useState(false);
  const [authMessage, setAuthMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [flashConfig, setFlashConfig] = useState(null);
  const [navConfig, setNavConfig] = useState(null);
  const [searchConfig, setSearchConfig] = useState(null);
  const [cartConfig, setCartConfig] = useState(null);
  const [footerConfig, setFooterConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${config.API_URL}/api/pullusers/${token}`);
      if (res.data.status === 0) {
      } else {
        setUser(res.data.user);
        if (res.data.user?.is_confirmed == 0) {
          setIsOtp(true);
        }
        localStorage.setItem('userInfor', JSON.stringify(res.data.user));
      }
    } catch (error) {
      localStorage.removeItem('userInfor');
      setUser(null);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthMessage(null);

    try {
      await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-XSRF-TOKEN': decodeURIComponent(xsrfToken || ''),
      };

      const response = await axios.post(
        `${config.API_URL}/api/verifyOtp`,
        { otp: otp, token: token },
        { headers, withCredentials: true }
      );

      if (response.data.status == '1') {
        setAuthMessage('Otp confirmed successful! Redirecting...');
        if (cartItems.length > 0) {
          setTimeout(() => navigate('/Checkout'), 1200);
        } else {
          setTimeout(() => navigate('/Shop/all'), 1200);
        }
      } else if (response.data.status == '5') {
        setAuthMessage('Otp confirmed successful! You can change your password from your account');
        setTimeout(() => navigate('/myAccount'), 1500);
      } else {
        setAuthMessage('Invalid OTP.');
      }
    } catch (err) {
      setAuthMessage('Invalid Credentials.');
      console.error('Auth error:', err);
    } finally {
      setAuthLoading(false);
    }
  };

  const getCachedData = () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY_LAYOUT);
      if (!cached) return null;
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp > CACHE_TTL) {
        localStorage.removeItem(CACHE_KEY_LAYOUT);
        return null;
      }
      return data;
    } catch {
      return null;
    }
  };

  const setCachedData = (data) => {
    try {
      const payload = { data, timestamp: Date.now() };
      localStorage.setItem(CACHE_KEY_LAYOUT, JSON.stringify(payload));
    } catch (e) {
    }
  };

  const toggleMode = () => {
    setIsLoginMode(prev => !prev);
    setFormErrors({});
    setAuthMessage(null);
    setFormData({ fullName: '', email: '', phone: '', password: '', confirmPassword: '' });
  };

  const handleAuthChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateAuthForm = () => {
    const errs = {};
    if (!isLoginMode) {
      if (!formData.fullName?.trim()) errs.fullName = 'Full name is required';
      if (!formData.phone?.trim()) errs.phone = 'Phone number is required';
      if (formData.password !== formData.confirmPassword) {
        errs.confirmPassword = 'Passwords do not match';
      }
    }
    if (!formData.email?.trim()) errs.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errs.email = 'Invalid email format';
    if (!formData.password) errs.password = 'Password is required';
    else if (formData.password.length < 6) errs.password = 'Min 6 characters';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const send_otp = async () => {
    setAuthLoading(true);
    setAuthMessage(null);

    try {
      await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-XSRF-TOKEN': decodeURIComponent(xsrfToken || ''),
      };

      const res = await axios.post(
        `${config.API_URL}/api/send_otp_v2`,
        { token: token },
        { headers, withCredentials: true }
      );
      if(res.data.status==1){
        setIsOtp(true);
      }else{
        setAuthMessage('Something went wrong. Re-try with the correct email');
        
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Something went wrong. Please try again.';
      setAuthMessage(message);
      console.error('Auth error:', err);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthMessage(null);

    try {
      await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-XSRF-TOKEN': decodeURIComponent(xsrfToken || ''),
      };

      await axios.post(
        `${config.API_URL}/api/send_otp_v2`,
        { email: forgotEmail },
        { headers, withCredentials: true }
      );

      setIsOtp(true);
    } catch (err) {
      const message = err.response?.data?.message || 'Something went wrong. Please try again.';
      setAuthMessage(message);
      console.error('Forgot error:', err);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    if (!validateAuthForm()) return;

    setAuthLoading(true);
    setAuthMessage(null);

    try {
      await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      let response;
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-XSRF-TOKEN': decodeURIComponent(xsrfToken || ''),
      };

      if (isLoginMode) {
        response = await axios.post(
          `${config.API_URL}/api/apiLogin`,
          { email: formData.email, password: formData.password },
          { headers, withCredentials: true }
        );
      } else {
        response = await axios.post(
          `${config.API_URL}/api/registerApi`,
          {
            name: formData.fullName,
            email: formData.email,
            contact: formData.phone,
            password: formData.password,
            confirmPassword: formData.confirmPassword,
          },
          { headers, withCredentials: true }
        );
      }

      const { token: newToken, user } = response.data;
      localStorage.setItem('token', newToken);

      if (isLoginMode) {
        localStorage.setItem('userInfor', response.data.user);
        setAuthMessage('Authentication successful! Redirecting...');
        if (cartItems.length > 0) {
          setTimeout(() => navigate('/Checkout'), 1200);
        } else {
          setTimeout(() => navigate('/Shop/all'), 1200);
        }
      } else {
        setAuthMessage('Authentication successful! Confirm OTP...');
        setIsOtp(true);
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Something went wrong. Please try again.';
      setAuthMessage(message);
      console.error('Auth error:', err);
    } finally {
      setAuthLoading(false);
    }
  };

  if (user?.sessionKey.length < 1) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6 text-center max-w-md mx-auto">
        <div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Already logged In</h2>
          <a
            onClick={() => navigate('/shop/all')}
            className="inline-block text-white px-8 py-4 rounded-xl font-semibold transition"
            style={{ backgroundColor: featuredBg }}
          >
            Back to Shopping
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <ShareableNav />
      <main className="flex-grow">
        <AuthForm
          isLoginMode={isLoginMode}
          formData={formData}
          setFormData={setFormData}
          formErrors={formErrors}
          setFormErrors={setFormErrors}
          authLoading={authLoading}
          authMessage={authMessage}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          showConfirmPassword={showConfirmPassword}
          setShowConfirmPassword={setShowConfirmPassword}
          handleAuthSubmit={handleAuthSubmit}
          handleOtpSubmit={handleOtpSubmit}
          toggleMode={toggleMode}
          handleAuthChange={handleAuthChange}
          isOtp={isOtp}
          setIsOtp={setIsOtp}
          send_otp={send_otp}
          otp={otp}
          setOtp={setOtp}
          featuredBg={featuredBg}
          userInfo={userInfo}
          isForgot={isForgot}
          setIsForgot={setIsForgot}
          forgotEmail={forgotEmail}
          setForgotEmail={setForgotEmail}
          handleForgotSubmit={handleForgotSubmit}
        />
      </main>
      {footerConfig && <FooterSection config={footerConfig} />}
    </div>
  );
};

export default ShopPage;